/* eslint-disable no-debugger */
import { useState, useEffect } from 'react';

import useForm from '@usas/ui-web/utils/UseForm';
import useReportPeriodFlatData from '@usas/ui-web/state/reportPeriodFlat/UseReportPeriodFlatData';
import Constants from '@usas/ui-web/utils/Constants';
import ToIntIfInt from '@usas/ui-web/utils/ToIntIfInt';
import useOrgRoleData from '@usas/ui-web/state/orgRole/UseOrgRoleData';

import useOrgUnitData from '../../../state/orgUnit/UseOrgUnitData';

import validate from "./RetentionDetailReportValidate";

// Define the columns configuration for this report
const columns = [
  { width: "90px", id: "SeasonYear", field: "seasonYear", title: "Season Year" },
  { width: "150px", id: "MemberId", field: "memberId", title: "Member ID" },
  { width: "120px", id: "FirstName", field: "firstName", title: "First Name" },
  { width: "120px", id: "MiddleName", field: "middleName", title: "Middle Name" },
  { width: "120px", id: "LastName", field: "lastName", title: "Last Name" },
  { width: "160px", id: "EventGender", field: "eventGender", title: "Competition Category" },
  { width: "90px", id: "LscCode", field: "lscCode", title: "LSC" },
  { width: "100px", id: "ClubCode", field: "clubCode", title: "Club Code" },
  { width: "240px", id: "ClubName", field: "clubName", title: "Club Name" },
  { width: "160px", id: "MemberCategory", field: "memberCategory", title: "Member Category" },
  { width: "100px", id: "SwimmerAge", field: "swimmerAge", title: "Age" },
  { width: "110px", id: "ClubRetained", field: "clubRetained", title: "Club Retained" },
  { width: "110px", id: "LscRetained", field: "lscRetained", title: "LSC Retained" },
  { width: "110px", id: "NatlRetained", field: "natlRetained", title: "Natl Retained" }
];

const excelColumns = [
  { id: "LscCode", field: "lscCode", title: "LSC" },
  { id: "ClubCode", field: "clubCode", title: "Club Code" },
  { id: "ClubName", field: "clubName", title: "Club Name" },
  { id: "MemberId", field: "memberId", title: "Member ID" },  
  { id: "FirstName", field: "firstName", title: "First Name" },
  { id: "MiddleName", field: "middleName", title: "Middle Name" },
  { id: "LastName", field: "lastName", title: "Last Name" },
  { id: "EventGender", field: "eventGender", title: "Competition Category" },
  { id: "SwimmerAge", field: "swimmerAge", title: "Age" },
  { id: "SeasonYear", field: "seasonYear", title: "Season Year" },
  { id: "MemberCategory", field: "memberCategory", title: "Member Category" },
  { id: "ClubRetained", field: "clubRetained", title: "Club Retained" },
  { id: "LscRetained", field: "lscRetained", title: "LSC Retained" },
  { id: "NatlRetained", field: "natlRetained", title: "Natl Retained" },
  { id: "RetentionDetail_ContactName", field: "retentionDetail_contactName", title: "Contact Name" },
  { id: "RetentionDetail_ContactEmail", field: "retentionDetail_contactEmail", title: "Contact Email" },
  { id: "RetentionDetail_Relationship", field: "retentionDetail_relationship", title: "Relationship" },
  { id: "RetentionDetail_PrimaryContact", field: "retentionDetail_primaryContact", title: "Primary Contact" }
];

// TODO: get report id from env varriable 
const REPORT_INFO_ID = 292;

const useRetentionDetailReport = () => {
  const { reportPeriodFlatState, getReportPeriodsFlatByOrganizationId } = useReportPeriodFlatData();
  const [ seasonYearState, setSeasonYearState ] = useState({ isLoading: true, isLoaded: false, options: [] });
  const [ memberTypeState, setMemberTypeState ] = useState({ isLoading: true, isLoaded: false, options: [] });

  const [ modalState, setModalState ] = useState(getInitialModalState());

  const { orgUnitState } = useOrgUnitData();
  const { orgRoleState } = useOrgRoleData();

  const {
    formState,
    errorState,
    handleSubmit,
    updateFormState,
    onFormValueChanged,
    onValueTextPairChanged,
    setFormState,
    setIsDirty
  } = useForm(getInitialFormState(), submitFormCallback, validate);

  function submitFormCallback() {
    setModalState({ ...modalState, displayPopUp: true });
  }

  const onRunReportClicked = (e) => {
    handleSubmit(e);
  }

  const onClearForm = () => {
    setFormState(getInitialFormState());
  }

  const onModalCancelClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setIsDirty(true);
    setModalState({ ...modalState, displayPopUp: false });
  };

  function getInitialModalState() {
    return {
      displayPopUp: false,
      currentPrimaryInfo: '',
      modalTitle: 'Report Viewer'
    };
  }

  function getInitialFormState() {
    return {
      orgUnitId: '',
      seasonId: Constants.DEFAULT_ID,
      seasonYear: Constants.DEFAULT_ID,
      minAge: 1,
      maxAge: 99,
      minTimeStandard: 12,
      maxTimeStandard: 1,
      genders: [{ id: 1, name: 'Female' }, { id: 2, name: 'Male' }],
      clubs: [],
      lscs: [],
      zones: [],
      memberTypes: [],
      meetTypes: [],
      exportDetailData: true
    };
  }

  useEffect(() => {
    if (reportPeriodFlatState.isLoading === false && reportPeriodFlatState.isLoaded === false) {
      getReportPeriodsFlatByOrganizationId(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (reportPeriodFlatState.isLoaded === true) {
      const years = [];

      reportPeriodFlatState.dataAsOptionsWithNames.map(rp => {
        if (rp.name.substring(5, 6) === '(') {
          years.unshift({ id: rp.id, name: rp.name.substring(0, 4) });
        }
      });
      years.unshift({ id: -1, name: '--' });
      setSeasonYearState({ isLoading: false, isLoaded: true, options: years });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportPeriodFlatState]);

  useEffect(() => {
    if (orgRoleState.isArrayLoaded === true && formState.memberTypes.length > 0) {
      const memtypes = [];

      formState.memberTypes.map(ft => {
        const found = orgRoleState.arrayData.find(mt => mt.orgRoleId === ft.id);
        if (found) {
          memtypes.push({ id: found.orgRoleId, name: found.orgRoleName });
        }
      });

      setMemberTypeState({ isLoading: false, isLoaded: true, options: memtypes });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgRoleState, formState.memberTypes]);

  function getClubFileterIds() {
    if (orgUnitState.arrayData.find(ou => ou.orgUnitId === formState.orgUnitId)?.orgLevelId === 2) { 
      return formState.orgUnitId.toString();
    } else  {
      return formState.lscs.map(c => c.id).join(',')
    } 
  }

  return {
    formState,
    errorState,
    reportParameters: {
      OrgUnitId_Int: formState.orgUnitId === "" ? null : formState.orgUnitId,
      SeasonYear_Int: formState.seasonYear === Constants.DEFAULT_ID ? null : parseInt(formState.seasonYear, 10), 
      MinAge_Int: ToIntIfInt(formState.minAge), 
      MaxAge_Int: ToIntIfInt(formState.maxAge), 
      Genders: formState.genders.map(gender => gender.id).join(','), 
      Clubs: formState.clubs.map(club => club.id).join(',') || null, 
      LSCs: formState.lscs.map(lsc => lsc.id).join(',') || null, 
      Zones: formState.zones.map(zone => zone.id).join(',').length === 0 ? null : formState.zones.map(zone => zone.id).join(','), 
      MeetTypes: formState.meetTypes.map(meetType => meetType.name).join(',') || null,
      MemberTypes: memberTypeState.options.map(mt => mt.name).join(',') || null
    },
    parameters: formState,
    columns: columns,
    modalState,
    orgUnitState,
    orgRoleState,
    onModalCancelClicked,
    handleSubmit,
    updateFormState,
    onFormValueChanged,
    onValueTextPairChanged,
    onRunReportClicked,
    onClearForm,
    seasonYearState,
    excelColumns,
    clubFilterIds: getClubFileterIds(),
    REPORT_INFO_ID
  };
};

export default useRetentionDetailReport;